// [C35] Builders from each single-operation voucher's A4 props to the 80 mm voucher (D-149): the container prepares
// one set of props from the server's payload; the print dialog's format switch chooses the template, so the 80 mm and
// the A4 copy always carry the same figures and the same document number (D-147).
import { createT } from '@faylasuf/i18n';
import type { CustomerEntryVoucherA4Props } from './CustomerEntryVoucherA4.js';
import type { TransferVoucherA4Props } from './TransferVoucherA4.js';
import type { SalesInvoiceA4Props } from './SalesInvoiceA4.js';
import type { InstallmentReceiptA4Props } from './InstallmentReceiptA4.js';
import type { Voucher80Props, Voucher80Row } from './Voucher80.js';
import type { PrintOffice } from './StatementA4.js';

export type VoucherFormat = 'a4' | '80mm';

export function entryVoucher80(p: CustomerEntryVoucherA4Props, office: PrintOffice, printedLabel?: string | null): Voucher80Props {
  const t = createT(p.locale);
  const two = p.lines.length > 1;
  const rows: Voucher80Row[] = [{ label: t('print.entry.row_movement'), value: t(`print.entry.movement.${p.subject}`) }];
  for (const l of p.lines) {
    const sfx = two ? ` (${l.currency})` : '';
    rows.push({ label: `${t('print.entry.row_before')}${sfx}`, value: l.beforeLabel, mono: true });
    rows.push({ label: `${t('print.entry.row_amount', { kind: p.payment ? t('print.entry.kind_payment') : t('print.entry.kind_add') })}${sfx}`, value: l.amountLabel, mono: true, strong: true });
    rows.push({ label: `${t('print.entry.row_after')}${sfx}`, value: l.afterLabel, mono: true, strong: true });
  }
  if (p.boxLabel) rows.push({ label: t('print.entry.row_box'), value: p.boxLabel });
  return {
    locale: p.locale,
    office: { ...office, name: p.officeName || office.name },
    title: `${t('print.entry.subject_prefix')} ${t(`print.entry.subject.${p.subject}`)}`,
    numberLabel: p.numberLabel,
    copy: p.copy,
    dateLabel: p.dateLabel,
    timeLabel: p.timeLabel,
    printedLabel: printedLabel ?? null,
    party: { label: t('print.v80.customer'), name: p.customerName, sub: p.accountNumber },
    rows,
    words: p.words ?? [],
    note: p.note,
    operator: p.operatorCode,
  };
}

export function transferVoucher80(p: TransferVoucherA4Props, office: PrintOffice, printedLabel?: string | null): Voucher80Props {
  const t = createT(p.locale);
  const rows: Voucher80Row[] = [
    { label: t('print.v80.transfer_code'), value: p.code, mono: true },
    { label: t('print.v80.direction'), value: p.direction === 'sent' ? t('print.voucher.type_out') : t('print.voucher.type_in') },
    { label: t('print.v80.destination'), value: p.destination },
    { label: t('print.v80.document'), value: p.documentId },
    { label: t('print.v80.source'), value: p.route === 'vault' ? t('print.voucher.source_vault') : `${t('print.voucher.tab_office')} - ${p.partnerName ?? ''}` },
    { label: t('print.v80.payment'), value: p.paymentLabel },
  ];
  if (p.customer) rows.push({ label: p.customer.kind === 'credit' ? t('print.voucher.client_credit') : t('print.voucher.client_trust'), value: p.customer.name });
  const totals: Voucher80Row[] = [{ label: t('print.voucher.principal'), value: p.principalLabel }];
  if (p.commissionLabel !== null) totals.push({ label: t('print.voucher.commission'), value: `+${p.commissionLabel}` });
  if (p.partnerFeeLabel !== null) totals.push({ label: p.partnerFeeIncoming ? t('print.voucher.partner_fee_in') : t('print.voucher.partner_fee'), value: `+${p.partnerFeeLabel}` });
  if (p.paidToBeneficiaryLabel !== null) totals.push({ label: t('print.voucher.paid_to_beneficiary'), value: p.paidToBeneficiaryLabel });
  if (p.settlement !== null) {
    totals.push({ label: t('print.voucher.settlement_cash'), value: p.settlement.cashLabel, strong: true });
    if (p.settlement.rateLabel !== null) totals.push({ label: t('print.voucher.settlement_rate'), value: p.settlement.rateLabel });
  }
  return {
    locale: p.locale,
    office: { ...(p.office ?? office), name: p.officeName || office.name },
    title: t('print.v80.transfer_title'),
    numberLabel: p.voucherNumber !== null ? `REC-${p.voucherNumber}` : null,
    copy: p.copy,
    dateLabel: p.dateLabel,
    printedLabel: printedLabel ?? null,
    party: { label: t('print.v80.beneficiary'), name: p.beneficiaryName },
    rows,
    amount: { label: t('print.voucher.total'), value: p.totalLabel },
    totals,
    words: p.words ?? [],
    operator: p.operatorName,
  };
}

export function saleInvoice80(p: SalesInvoiceA4Props, operator: string, printedLabel?: string | null): Voucher80Props {
  const t = createT(p.locale);
  const totals: Voucher80Row[] = [];
  for (const s of p.summary ?? []) totals.push({ label: s.label, value: String(s.value) });
  if (p.receivedLabel) totals.push({ label: t('print.sale.received'), value: p.receivedLabel });
  if (p.remainingLabel) totals.push({ label: t('print.sale.remaining'), value: p.remainingLabel, strong: true });
  return {
    locale: p.locale,
    office: p.office,
    title: p.title,
    numberLabel: p.docNumber,
    copy: false, // the title already says "نسخة" on a reprint (D-147, D-323)
    dateLabel: p.dateLabel,
    printedLabel: printedLabel ?? null,
    party: { label: t('print.sale.buyer'), name: p.buyerName, sub: p.buyerPhone ?? null },
    rows: [{ label: t('print.sale.payment'), value: p.paymentMethodLabel }],
    items: p.lines,
    amount: { label: t('print.sale.grand_total'), value: p.grandTotalLabel },
    totals,
    words: p.wordsLabel ? [p.wordsLabel] : [],
    operator,
  };
}

export function installmentReceipt80(p: InstallmentReceiptA4Props, printedLabel?: string | null): Voucher80Props {
  const t = createT(p.locale);
  const rows: Voucher80Row[] = [
    { label: t('print.receipt.row_contract'), value: p.contractNumber, mono: true },
    { label: t('print.receipt.row_item'), value: p.itemName },
  ];
  if (p.boxLabel) rows.push({ label: t('print.entry.row_box'), value: p.boxLabel });
  return {
    locale: p.locale,
    office: p.office,
    title: `${t('print.entry.subject_prefix')} ${t('print.receipt.subject')}`,
    numberLabel: p.numberLabel,
    copy: p.copy,
    dateLabel: p.dateLabel,
    timeLabel: p.timeLabel,
    printedLabel: printedLabel ?? null,
    party: { label: t('print.v80.customer'), name: p.customerName, sub: p.accountNumber },
    rows,
    amount: { label: t('print.receipt.row_amount'), value: p.amountLabel },
    totals: [
      { label: t('print.receipt.row_paid_total'), value: p.paidTotalLabel },
      { label: t('print.receipt.row_remaining'), value: p.remainingLabel, strong: true },
    ],
    words: p.wordsLabel ? [p.wordsLabel] : [],
    note: p.note,
    operator: p.operatorCode,
  };
}
